import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth, SignInButton } from "@clerk/react";
import { useBillingStatus } from "../hooks/useBillingStatus.js";
import { startCheckout, getBillingPortal } from "../api.js";
import styles from "../styles/pages/PremiumPage.module.css";
import { FlatLogo } from "../components/FlatLogo.js";
import {
  useTheme,
  THEMES,
  DARK_TEXT,
  LIGHT_TEXT,
} from "../state/themeState.js";

type PlanId = "free" | "plus" | "pro";

const PLANS: Array<{
  id: PlanId;
  name: string;
  price: string;
  period: string;
  tagline: string;
  features: string[];
  cta: string;
  highlight: boolean;
}> = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    tagline: "Curated discovery",
    features: [
      "25 curated system rooms",
      "Up to 3 personal rooms",
      "Read state sync",
      "Public room sharing",
      "RSS / widget embed",
    ],
    cta: "Current plan",
    highlight: false,
  },
  {
    id: "plus",
    name: "Plus",
    price: "$5",
    period: "per month",
    tagline: "Your own feed, your way",
    features: [
      "Everything in Free",
      "50 personal rooms",
      "100 custom RSS feeds",
      "OPML import & export",
      "Twice-daily email digest",
      "Keyword filters (block / highlight)",
    ],
    cta: "Upgrade to Plus",
    highlight: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: "$15",
    period: "per month",
    tagline: "Power-user toolkit",
    features: [
      "Everything in Plus",
      "Unlimited rooms & feeds",
      "AI post summaries",
      "Webhooks & automation rules",
      "Slack / Discord / Teams integrations",
      "Team workspaces",
    ],
    cta: "Upgrade to Pro",
    highlight: true,
  },
];

export function PremiumPage() {
  const { isSignedIn } = useAuth();
  const { themeIndex, hoveredThemeIndex, isDark } = useTheme();
  const activeColors =
    THEMES[hoveredThemeIndex ?? themeIndex]?.[isDark ? "dark" : "light"];
  const navigate = useNavigate();
  const { status, invalidate } = useBillingStatus();
  const currentPlan: PlanId = (status?.capabilities.plan ?? "free") as PlanId;
  const [busy, setBusy] = useState<PlanId | null>(null);

  async function handleUpgrade(planId: PlanId) {
    if (planId === "free" || planId === currentPlan) return;
    setBusy(planId);
    try {
      const url = await startCheckout(planId);
      await invalidate();
      navigate({ to: url as "/" });
    } catch {
      // ignore
    } finally {
      setBusy(null);
    }
  }

  async function handleManage() {
    setBusy("free");
    try {
      const url = await getBillingPortal();
      window.location.href = url;
    } catch {
      setBusy(null);
    }
  }

  return (
    <div className={styles.page}>
      <nav className={styles.topbar}>
        <Link to="/" className={styles.back}>
          ← Feed
        </Link>
      </nav>

      <div className={styles.inner}>
        <div className={styles.hero}>
          <FlatLogo
            size={32}
            text="Twice Daily"
            separation={76}
            accentColor={activeColors?.primary}
            textColor={isDark ? DARK_TEXT : LIGHT_TEXT}
          />
          <h1 className={styles.heading}>Simple, honest pricing</h1>
          <p className={styles.sub}>
            One free tier and two paid plans. No feature creep, no dark
            patterns.
          </p>
        </div>

        <div className={styles.plansGrid}>
          {PLANS.map((plan) => {
            const isCurrent = isSignedIn && plan.id === currentPlan;
            const isDowngrade =
              isSignedIn &&
              ((currentPlan === "pro" && plan.id !== "pro") ||
                (currentPlan === "plus" && plan.id === "free"));
            return (
              <div
                key={plan.id}
                className={`${styles.planCard} ${plan.highlight ? styles.planCardHighlight : ""}`}
              >
                {plan.highlight && (
                  <div className={styles.badge}>Most popular</div>
                )}
                <div className={styles.planHeader}>
                  <p className={styles.planName}>{plan.name}</p>
                  <p className={styles.planTagline}>{plan.tagline}</p>
                  <div className={styles.planPrice}>
                    <span className={styles.planAmount}>{plan.price}</span>
                    <span className={styles.planPeriod}>{plan.period}</span>
                  </div>
                </div>
                <ul className={styles.featureList}>
                  {plan.features.map((f) => (
                    <li key={f} className={styles.feature}>
                      <span className={styles.check}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <div className={styles.planFooter}>
                  {!isSignedIn ? (
                    plan.id === "free" ? (
                      <SignInButton mode="modal">
                        <button type="button" className={styles.ctaBtn}>
                          Sign in for free
                        </button>
                      </SignInButton>
                    ) : (
                      <SignInButton mode="modal">
                        <button
                          type="button"
                          className={`${styles.ctaBtn} ${plan.highlight ? styles.ctaBtnPrimary : ""}`}
                        >
                          {plan.cta}
                        </button>
                      </SignInButton>
                    )
                  ) : isCurrent ? (
                    currentPlan !== "free" ? (
                      <button
                        type="button"
                        className={styles.ctaBtnOutline}
                        onClick={handleManage}
                        disabled={busy === "free"}
                      >
                        Manage subscription
                      </button>
                    ) : (
                      <span className={styles.currentLabel}>
                        Your current plan
                      </span>
                    )
                  ) : isDowngrade ? (
                    <button
                      type="button"
                      className={styles.ctaBtnOutline}
                      onClick={handleManage}
                    >
                      Downgrade
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`${styles.ctaBtn} ${plan.highlight ? styles.ctaBtnPrimary : ""}`}
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={busy === plan.id}
                    >
                      {busy === plan.id ? "Redirecting…" : plan.cta}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className={styles.footnote}>
          Prices in USD. Cancel anytime. Questions?{" "}
          <a href="mailto:hello@twice-daily.com" className={styles.footLink}>
            hello@twice-daily.com
          </a>
        </p>
      </div>
    </div>
  );
}
